import { Globe2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { StatusBadge } from '@/shared/components/status-badge';

import { useCountries } from '../api/pricing.queries';

/**
 * Countries (markets) in the staff member's scope and how each is set up to
 * sell. A handful of rows, so no paging.
 */
export function CountriesPage() {
  const countries = useCountries();

  return (
    <PageContainer>
      <PageHeader
        title="Countries"
        description="Markets you work in: currency, VAT, live pricing, payment channels and what is offered."
      />
      {countries.isLoading ? (
        <Skeleton className="h-40" />
      ) : countries.error ? (
        <ErrorState error={countries.error} onRetry={() => void countries.refetch()} />
      ) : (countries.data ?? []).length === 0 ? (
        <EmptyState
          icon={Globe2}
          title="No countries"
          description="No country is assigned to you."
        />
      ) : (
        <div className="border-border overflow-x-auto rounded-md border">
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Country</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead className="text-end">VAT</TableHead>
                <TableHead>Pricing</TableHead>
                <TableHead className="text-end">Channels on</TableHead>
                <TableHead className="text-end">Items offered</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(countries.data ?? []).map((country) => (
                <TableRow key={country.id}>
                  <TableCell>
                    <Link
                      to={`/countries/${country.id}`}
                      className="text-primary font-medium hover:underline"
                    >
                      {country.nameEn ?? country.code}
                    </Link>
                    <span className="text-muted-foreground ms-1 font-mono">{country.code}</span>
                  </TableCell>
                  <TableCell>
                    <StatusBadge variant={country.isActive ? 'success' : 'neutral'}>
                      {country.isActive ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  </TableCell>
                  <TableCell className="font-mono">
                    {country.currencyCode}
                    {country.settlementCurrency &&
                    country.settlementCurrency !== country.currencyCode
                      ? ` → ${country.settlementCurrency}`
                      : ''}
                  </TableCell>
                  <TableCell className="text-end tabular-nums">{country.vat}%</TableCell>
                  <TableCell>
                    <span className="flex flex-wrap gap-1">
                      {country.activePricingVersion === null ? (
                        <StatusBadge variant="danger">No live pricing</StatusBadge>
                      ) : (
                        <StatusBadge variant="success">
                          Live v{country.activePricingVersion}
                        </StatusBadge>
                      )}
                      {country.hasPendingApproval && (
                        <StatusBadge variant="info">Awaiting approval</StatusBadge>
                      )}
                      {!country.hasPendingApproval && country.hasPricingDraft && (
                        <StatusBadge variant="neutral">Draft</StatusBadge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-end tabular-nums">
                    {country.activeChannelCount}
                  </TableCell>
                  <TableCell className="text-end tabular-nums">{country.offeredCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </PageContainer>
  );
}
